/* ──────────────────────────────────────────────────────────────
   src/pages/reports/profesores/VacacionesPage.tsx
   Informe VACACIONES · lista por profesor + detalle desplegable
   ────────────────────────────────────────────────────────────── */
import { useState, useMemo } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
/* ── MUI ─────────────────────────────────────────── */
import {
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  LinearProgress,
  Alert,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Collapse,
  IconButton,
  Box,
  InputAdornment,
} from "@mui/material";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";

/* ── hooks + tipos ───────────────────────────────── */
import { useVacacionesProfesorReport } from "../../../hooks/useReportJob";
import type { VacProfRow, VacDetRow } from "../../../api/reports";
import { downloadReportFile } from "../../../components/utils/downloadXlsx";

/* rango de años 2020-2029 (ajústalo si quieres) */
const YEARS = Array.from({ length: 10 }, (_, i) => 2020 + i);

/* ═════ componente fila + colapso ═════ */
function Row({ row }: { row: VacProfRow }) {
  const { t } = useTranslation<"translation">();
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* cabecera */}
      <TableRow>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen((o) => !o)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{row.profesor}</TableCell>
        <TableCell align="right">{row.diasN}</TableCell>
        <TableCell>{dayjs(row.ultima).format("DD-MM-YYYY")}</TableCell>
      </TableRow>

      {/* detalle */}
      <TableRow>
        <TableCell colSpan={4} sx={{ p: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("vacaciones.detalle.fecha")}</TableCell>
                    <TableCell>{t("vacaciones.detalle.creado")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.detalle.map((d: VacDetRow, i: number) => (
                    <TableRow key={i}>
                      <TableCell>{dayjs(d.fecha).format("DD-MM-YYYY")}</TableCell>
                      <TableCell>{dayjs(d.creado).format("DD-MM-YYYY")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/* ═════ componente principal ═════ */
export default function VacacionesPage() {
  const { t } = useTranslation<"translation">();
  const currentYear = dayjs().year();
  const [year, setYear] = useState(currentYear);
  const [search, setSearch] = useState("");

  /* worker */
  const { run, useStatus } = useVacacionesProfesorReport();
  const [jobId, setJobId] = useState<string>();
  const status = useStatus(jobId).data;

  /* lanzar informe */
  const generar = async () => {
    const { jobId } = await run({ year });
    setJobId(jobId);
  };

  const handleDownload = async () => {
    if (status?.result?.url) {
      try {
        await downloadReportFile(status.result.url);
      } catch (error) {
        console.error("Error al descargar:", error);
      }
    }
  };

  /* filas + filtro ----------------------------- */
  const rows: VacProfRow[] = status?.result?.rows ?? [];
  const rowsFiltradas = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter((r) => r.profesor.toLowerCase().includes(q)) : rows;
  }, [rows, search]);

  /* UI ----------------------------------------- */
  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("vacaciones.titulo", { year })}</Typography>

      {/* barra superior */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          select
          label={t("vacaciones.filtros.anio")}
          value={year}
          onChange={(e) => setYear(+e.target.value)}
        >
          {YEARS.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>

        <Button variant="contained" onClick={generar}>
          {t("vacaciones.filtros.generar")}
        </Button>

        {/* buscador: sólo si hay datos */}
        {rows.length > 0 && (
          <TextField
            placeholder={t<string>("vacaciones.filtros.buscar")}
            size="small"
            sx={{ ml: "auto", maxWidth: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Stack>

      {/* progreso */}
      {status && !status.done && <LinearProgress variant="determinate" value={status.progress} />}

      {/* resultado */}
      {status?.done && (
        <>
          <Alert severity="success">
            {t("vacaciones.estado.exito")}{" "}
            <Button
              variant="text"
              size="small"
              onClick={handleDownload}
              sx={{ textTransform: "none", textDecoration: "underline", p: 0, minWidth: "auto" }}
            >
              {t("vacaciones.estado.descargar")}
            </Button>
          </Alert>

          <Paper sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell /> {/* colapso */}
                  <TableCell>{t("vacaciones.tabla.profesor")}</TableCell>
                  <TableCell align="right">{t("vacaciones.tabla.dias")}</TableCell>
                  <TableCell>{t("vacaciones.tabla.ultimo")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rowsFiltradas.map((r) => (
                  <Row key={r.profesorId} row={r} />
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Stack>
  );
}
